// src/components/Overview.jsx
// verticalstack — S32 Layer 2 Overview (first dashboard absorption)
//
// Replaces PlaceholderDashboard for the /overview route only — dispatched
// from PlaceholderDashboard.jsx based on section prop. Other nav sections
// (Portfolio / Operate / Maintain) continue to render the Layer 1
// placeholder until their own Layer 2+ absorption sessions.
//
// PROPS (from Shell via PlaceholderDashboard dispatcher)
//   workspace  : current workspace doc (already hydrated by useWorkspace)
//   roles      : array of role strings for this user × this workspace
//   roleLabel  : human-readable role label (already formatted by Shell)
//
// Absorbed content (from OwnerApp.js, now orphaned per AS-K):
//   - Pending approvals widget   (OwnerApp lines 96-141)
//   - Recent notifications widget (OwnerApp lines 143-170)
//
// Per AS-Q: legacy approval/notification CFs (approveRepairRequest,
// declineRepairRequest, markNotificationRead, getOwnerPendingApprovals,
// getUserNotifications) remain on old schema. Buttons render but fire
// "Coming in Layer 2.5" toasts. CF migration is its own session.
//
// // Per AS-R reconciled May 17: notifications collection top-level with recipientId field
// (matches deployed firestore.rules S37 v2 baseline).
// If actual data is at a different path, widget shows empty until reconciled.
//
// Per AS-S: workOrders collection assumed to be top-level with workspaceId
// and requiresOwnerApproval fields. Same fallback behavior if schema differs.
//
// V2 (S32 Row 2): aligned with PlaceholderDashboard conventions.
//   - Accepts workspace/roles/roleLabel as props from Shell (no redundant fetch)
//   - Imports db from '../firebase/config' (matches PlaceholderDashboard, not AdminApp)
//   - Uses getVerticalLabel from roles.js (no inline duplication)
//   - Drops AS-O fallback chain — Shell's currentWorkspace already normalized
//
// Branding deferred per memory rule. Inline neutral professional styles only.

import React, { useState, useEffect } from 'react';
import {
  collection, query, where, getDocs, orderBy, limit,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../hooks/useAuth';
import { getVerticalLabel } from '../config/roles';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(ts) {
  if (!ts) return '';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  } catch {
    return '';
  }
}

// ── Overview ─────────────────────────────────────────────────────────────────

export default function Overview({ workspace, roles, roleLabel }) {
  const { profile } = useAuth();
  const userId = profile?.id || profile?.uid;
  const workspaceId = workspace?.workspaceId;

  const [parcels, setParcels] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [notifications, setNotifications] = useState([]);

  const [parcelsLoading, setParcelsLoading] = useState(true);
  const [woLoading, setWoLoading] = useState(true);
  const [notifLoading, setNotifLoading] = useState(true);

  const [parcelsError, setParcelsError] = useState(null);

  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2800);
  };

  // ── Load parcels for this workspace ────────────────────────────────────────
  useEffect(() => {
    if (!workspaceId) {
      setParcels([]);
      setParcelsLoading(false);
      return;
    }
    let cancelled = false;
    setParcelsLoading(true);
    setParcelsError(null);
    const q = query(
      collection(db, 'parcels'),
      where('workspaceId', '==', workspaceId)
    );
    getDocs(q)
      .then((snap) => {
        if (cancelled) return;
        setParcels(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[Overview] parcels read failed:', err);
        setParcelsError(err.message || 'Failed to load parcels.');
      })
      .finally(() => {
        if (!cancelled) setParcelsLoading(false);
      });
    return () => { cancelled = true; };
  }, [workspaceId]);

  // ── Load work orders + approvals (AS-S) ────────────────────────────────────
  useEffect(() => {
    if (!workspaceId) {
      setWorkOrders([]);
      setApprovals([]);
      setWoLoading(false);
      return;
    }
    let cancelled = false;
    setWoLoading(true);
    const q = query(
      collection(db, 'workOrders'),
      where('workspaceId', '==', workspaceId)
    );
    getDocs(q)
      .then((snap) => {
        if (cancelled) return;
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Active = anything not completed/cancelled. Schema TBD; conservative filter.
        const active = all.filter(
          (wo) => wo.status !== 'completed' && wo.status !== 'cancelled'
        );
        setWorkOrders(active);
        // Pending approvals subset
        const pending = all.filter((wo) => wo.requiresOwnerApproval === true);
        setApprovals(pending);
      })
      .catch((err) => {
        if (cancelled) return;
        // AS-S: schema may not be migrated. Empty state is correct fallback.
        console.warn('[Overview] workOrders read returned error (AS-S):', err);
        setWorkOrders([]);
        setApprovals([]);
      })
      .finally(() => {
        if (!cancelled) setWoLoading(false);
      });
    return () => { cancelled = true; };
  }, [workspaceId]);

  // ── Load notifications (AS-R) ──────────────────────────────────────────────
  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setNotifLoading(false);
      return;
    }
    let cancelled = false;
    setNotifLoading(true);
    // AS-R: assume top-level notifications with userId field, ordered by createdAt desc
    const q = query(
      collection(db, 'notifications'),
    where('recipientId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(10)
    );
    getDocs(q)
      .then((snap) => {
        if (cancelled) return;
        setNotifications(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((err) => {
        if (cancelled) return;
        // AS-R: path may be wrong, or composite index missing. Empty state is correct fallback.
        console.warn('[Overview] notifications read returned error (AS-R):', err);
        setNotifications([]);
      })
      .finally(() => {
        if (!cancelled) setNotifLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId]);

  // ── Stub handlers (AS-Q: CFs not migrated yet) ─────────────────────────────
  const handleApprove = (id) => {
    showToast('Approve action — coming in Layer 2.5');
  };
  const handleDecline = (id) => {
    showToast('Decline action — coming in Layer 2.5');
  };
  const handleMarkRead = (id) => {
    showToast('Mark-read — coming in Layer 2.5');
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!workspace) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>Loading workspace…</div>
      </div>
    );
  }

  const verticalLabel = workspace.verticalType
    ? getVerticalLabel(workspace.verticalType)
    : 'Property Management';

  return (
    <div style={styles.container}>
      {/* Section header — matches PlaceholderDashboard convention */}
      <header style={styles.sectionHeader}>
        <h1 style={styles.sectionTitle}>Overview</h1>
        <p style={styles.sectionDescription}>
          High-level snapshot of this workspace.
        </p>
      </header>

      {/* ── Widget 1: Workspace summary ─────────────────────────────────── */}
      <section style={styles.card}>
        <h2 style={styles.cardHeader}>Workspace</h2>
        <div style={styles.factGrid}>
          <div style={styles.fact}>
            <span style={styles.factLabel}>Name</span>
            <span style={styles.factValue}>{workspace.workspaceName || '—'}</span>
          </div>
          <div style={styles.fact}>
            <span style={styles.factLabel}>Vertical</span>
            <span style={styles.factValue}>{verticalLabel}</span>
          </div>
          <div style={styles.fact}>
            <span style={styles.factLabel}>Plan</span>
            <span style={styles.factValue}>{workspace.plan || '—'}</span>
          </div>
          <div style={styles.fact}>
            <span style={styles.factLabel}>Your role</span>
            <span style={styles.factValue}>{roleLabel || '—'}</span>
          </div>
        </div>
      </section>

      {/* ── Widget 2: Quick stats ────────────────────────────────────────── */}
      <section style={styles.card}>
        <h2 style={styles.cardHeader}>Quick stats</h2>
        <div style={styles.statsGrid}>
          <div style={styles.statBlock}>
            <div style={styles.statValue}>
              {parcelsLoading ? '…' : parcels.length}
            </div>
            <div style={styles.statLabel}>Parcels</div>
          </div>
          <div style={styles.statBlock}>
            <div style={styles.statValue}>
              {woLoading ? '…' : workOrders.length}
            </div>
            <div style={styles.statLabel}>Active work orders</div>
          </div>
          <div style={styles.statBlock}>
            <div style={styles.statValue}>—</div>
            <div style={styles.statLabel}>Occupancy</div>
          </div>
        </div>
        {parcelsError && (
          <div style={{ ...styles.errorState, marginTop: 12 }}>
            {parcelsError}
          </div>
        )}
      </section>

      {/* ── Widget 3: Pending approvals ──────────────────────────────────── */}
      <section style={styles.card}>
        <h2 style={styles.cardHeader}>
          Pending approvals
          {approvals.length > 0 && (
            <span style={styles.countPill}>{approvals.length}</span>
          )}
        </h2>
        {woLoading ? (
          <div style={styles.loadingState}>Loading…</div>
        ) : approvals.length === 0 ? (
          <div style={styles.emptyBlock}>
            <div style={styles.emptyTitle}>All clear</div>
            <div style={styles.emptyBody}>
              No repair requests waiting for approval.
            </div>
          </div>
        ) : (
          approvals.map((a) => (
            <div key={a.id} style={styles.approvalRow}>
              {a.urgent && <div style={styles.urgentTag}>URGENT</div>}
              <div style={styles.approvalProp}>
                {a.parcelDisplayName || a.propertyAddress || a.parcelId || a.propertyId || 'Unknown property'}
              </div>
              <div style={styles.approvalDesc}>
                {a.issueType || 'Repair request'}
                {a.description ? ` — ${a.description}` : ''}
              </div>
              {(a.price || a.estimatedCost) && (
                <div style={styles.approvalPrice}>
                  ${a.price || a.estimatedCost}
                  <span style={styles.approvalPriceSub}> flat rate</span>
                  {a.urgent && (
                    <span style={styles.approvalCallout}> + $125 call-out</span>
                  )}
                </div>
              )}
              <div style={styles.approvalActions}>
                <button
                  type="button"
                  style={styles.approveBtn}
                  onClick={() => handleApprove(a.id)}
                >
                  Approve
                </button>
                <button
                  type="button"
                  style={styles.declineBtn}
                  onClick={() => handleDecline(a.id)}
                >
                  Decline
                </button>
              </div>
            </div>
          ))
        )}
      </section>

      {/* ── Widget 4: Recent notifications ───────────────────────────────── */}
      <section style={styles.card}>
        <h2 style={styles.cardHeader}>Recent notifications</h2>
        {notifLoading ? (
          <div style={styles.loadingState}>Loading…</div>
        ) : notifications.length === 0 ? (
          <div style={styles.emptyBlock}>
            <div style={styles.emptyTitle}>No notifications</div>
            <div style={styles.emptyBody}>You're all caught up.</div>
          </div>
        ) : (
          notifications.map((n) => (
            <div
              key={n.id}
              style={{
                ...styles.notifRow,
                opacity: n.read ? 0.6 : 1,
              }}
              onClick={() => handleMarkRead(n.id)}
            >
              <div
                style={{
                  ...styles.notifDot,
                  background: n.read ? '#D1D5DB' : '#003060',
                }}
              />
              <div style={styles.notifBody}>
                <div
                  style={{
                    ...styles.notifTitle,
                    fontWeight: n.read ? 400 : 600,
                  }}
                >
                  {n.title || 'Notification'}
                </div>
                {n.body && <div style={styles.notifText}>{n.body}</div>}
                {n.createdAt && (
                  <div style={styles.notifDate}>{formatDate(n.createdAt)}</div>
                )}
              </div>
            </div>
          ))
        )}
      </section>

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      {toast && <div style={styles.toast}>{toast}</div>}
    </div>
  );
}

// ── Styles (mirrors PlaceholderDashboard conventions) ──────────────────────

const styles = {
  container: {
    maxWidth: 1100,
    margin: '0 auto',
    fontFamily: 'Arial, sans-serif',
  },

  // Section header — matches PlaceholderDashboard
  sectionHeader: { marginBottom: 32 },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#003060',
    margin: 0,
    marginBottom: 4,
  },
  sectionDescription: { fontSize: 14, color: '#6B7280', margin: 0 },

  // Cards — matches PlaceholderDashboard
  card: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: 24,
    marginBottom: 20,
  },
  cardHeader: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    marginTop: 0,
    marginBottom: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },

  // Workspace summary — matches PlaceholderDashboard factGrid pattern
  factGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 20,
  },
  fact: { display: 'flex', flexDirection: 'column', gap: 4 },
  factLabel: {
    fontSize: 12,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontWeight: 600,
  },
  factValue: { fontSize: 18, color: '#003060', fontWeight: 700 },

  // Quick stats grid
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 12,
  },
  statBlock: {
    backgroundColor: '#F8F9FA',
    border: '1px solid #E5E7EB',
    borderRadius: 6,
    padding: 16,
    textAlign: 'center',
  },
  statValue: { fontSize: 28, fontWeight: 700, color: '#003060' },
  statLabel: {
    fontSize: 11,
    color: '#6B7280',
    marginTop: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontWeight: 600,
  },

  // Pending approvals
  countPill: {
    background: '#003060',
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 10,
  },
  approvalRow: { padding: '14px 0', borderBottom: '1px solid #F3F4F6' },
  urgentTag: {
    display: 'inline-block',
    background: '#991B1B',
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1,
    padding: '3px 8px',
    borderRadius: 4,
    marginBottom: 8,
  },
  approvalProp: { fontSize: 14, fontWeight: 600, color: '#003060' },
  approvalDesc: {
    fontSize: 13,
    color: '#4B5563',
    marginTop: 4,
    lineHeight: 1.5,
  },
  approvalPrice: {
    fontSize: 18,
    fontWeight: 700,
    color: '#003060',
    marginTop: 8,
    lineHeight: 1.4,
  },
  approvalPriceSub: { fontSize: 12, fontWeight: 400, color: '#6B7280' },
  approvalCallout: { fontSize: 12, color: '#B91C1C', fontWeight: 500 },
  approvalActions: { display: 'flex', gap: 8, marginTop: 12 },
  approveBtn: {
    flex: 1,
    padding: '10px 14px',
    background: '#003060',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  declineBtn: {
    flex: 1,
    padding: '10px 14px',
    background: '#FFFFFF',
    color: '#374151',
    border: '1px solid #D1D5DB',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  // Notifications
  notifRow: {
    display: 'flex',
    gap: 12,
    padding: '12px 0',
    borderBottom: '1px solid #F3F4F6',
    cursor: 'pointer',
  },
  notifDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginTop: 6,
    flexShrink: 0,
  },
  notifBody: { flex: 1, minWidth: 0 },
  notifTitle: { fontSize: 14, color: '#003060' },
  notifText: { fontSize: 13, color: '#4B5563', lineHeight: 1.5, marginTop: 2 },
  notifDate: { fontSize: 11, color: '#9CA3AF', marginTop: 4 },

  // Empty / loading / error states — mirrors PlaceholderDashboard naming
  loadingState: {
    color: '#9CA3AF',
    fontStyle: 'italic',
    fontSize: 14,
    padding: '8px 0',
  },
  errorState: {
    color: '#B91C1C',
    fontSize: 13,
    padding: '12px 16px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FCA5A5',
    borderRadius: 6,
  },
  emptyBlock: { textAlign: 'center', padding: '20px 16px' },
  emptyTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#003060',
    marginBottom: 4,
  },
  emptyBody: { fontSize: 13, color: '#6B7280' },

  // Toast
  toast: {
    position: 'fixed',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#003060',
    color: '#FFFFFF',
    padding: '10px 18px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    zIndex: 200,
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
};
